import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { debounceTime, filter, map } from 'rxjs/operators';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { ValueHelperInstanceToken, ValueHelperInterface } from '../../../interfaces/value-helper.interface';
import { selectEnableStatesOfUSADropdown } from '../../config/selectors/config.selector';
import { hydrateSlice, persistenceError, persistSlice, sliceHydrated, slicePersisted } from '../../persistence';
import {
    addAddressesRequested,
    addressesLoadedFromStorage,
    addressesSaved,
    addressesUnchanged,
    addressScopeRequested,
    addressStorageSyncError,
    addressUpserted,
    deleteAddressRequested,
    editAddressRequested,
    hideAddressRequested,
    initAddresses,
    resetAddressVisibility,
    saveAddressesRequested,
    upsertAddressRequested,
} from '../actions/address.actions';
import { ADDRESS_FEATURE_KEY, AddressState } from '../address-collection.state';
import {
    markAddressesOutOfScope,
    mergeNewAddressesIntoCollection,
    splitStateOfUSA,
    upsertAddress,
} from '../address-merge';
import { selectAddresses } from '../selectors/address.selector';

/**
 * Owns the address slice's write orchestration. The NgRx store is the source of truth:
 * mutations set the slice first (reducers), then this effect mirrors the collection to the
 * unified slice storage via the generic `persistSlice` / `hydrateSlice` layer (keyed by the
 * address feature). The generic `slicePersisted` / `persistenceError` / `sliceHydrated`
 * outcomes are bridged back to the address-specific settle actions so the facade's
 * correlation-id write contract is unchanged.
 *
 * Storage reads are one-shot: `loadAddresses$` re-hydrates on every `initAddresses` (that is
 * `refreshAddresses()`'s contract). The address collection persists through the generic
 * persistence layer's concurrency-store backend under the `addressSlice` key.
 */
@Injectable()
export class AddressEffects {
    private readonly actions$ = inject(Actions);
    private readonly store = inject(Store);
    private readonly valueHelperService = inject<ValueHelperInterface>(ValueHelperInstanceToken);
    private readonly log: LogInterface = inject(LogInstanceToken);

    /**
     * Persists the collection after an optimistic write (hide / reset /
     * delete / edit). Effects observe actions after the reducers ran, so the selector
     * already holds the updated collection; forwarding it under the same
     * correlationId lets the caller await this write's persistence outcome.
     */
    public persistUpdatedAddresses$ = createEffect(() =>
        this.actions$.pipe(
            ofType(hideAddressRequested, resetAddressVisibility, deleteAddressRequested, editAddressRequested),
            concatLatestFrom(() => this.store.select(selectAddresses)),
            map(([{ correlationId }, addresses]) => saveAddressesRequested({ addresses, correlationId })),
        ),
    );

    /**
     * Folds one address into the collection.
     *
     * The address is normalized first: the `<city>;<state>` suffix OData sends for a USA address is
     * split off here rather than by whoever fetched it, which is what lets every caller hand over an
     * address exactly as its source produced it. `upsertAddress` then places it — see there for what
     * counts as the same address and what an update keeps.
     *
     * The result is set on the slice synchronously, through `addressUpserted`: a caller that has to
     * know which entry its address ended up as reads the slice straight after dispatching.
     */
    public upsertAddress$ = createEffect(() =>
        this.actions$.pipe(
            ofType(upsertAddressRequested),
            concatLatestFrom(() => [
                this.store.select(selectAddresses),
                this.store.select(selectEnableStatesOfUSADropdown),
            ]),
            map(([{ address, correlationId }, addresses, splitStatesOfUSA]) =>
                addressUpserted({
                    addresses: upsertAddress(
                        addresses,
                        splitStatesOfUSA ? splitStateOfUSA(address, this.statesOfUSACodes()) : address,
                    ),
                    correlationId,
                }),
            ),
        ),
    );

    /**
     * Mirrors an upserted collection to storage.
     *
     * Coalesced, unlike the other persist paths: a notification load hands over its addresses one at
     * a time, and persisting compresses and writes the whole collection, so a burst of upserts is
     * written once it has settled rather than once per address. The write therefore settles under the
     * last `correlationId` of the burst — no caller of a single upsert awaits its own, and one that
     * has to should await `addressesSaved` rather than a correlation.
     */
    public persistUpsertedAddresses$ = createEffect(() =>
        this.actions$.pipe(
            ofType(addressUpserted),
            debounceTime(0),
            concatLatestFrom(() => this.store.select(selectAddresses)),
            map(([{ correlationId }, addresses]) => saveAddressesRequested({ addresses, correlationId })),
        ),
    );

    /** The USA state codes the value help currently recognizes. */
    private statesOfUSACodes(): string[] {
        return this.valueHelperService.getStatesOfUSA().map((state) => state.code);
    }

    public mergeAddresses$ = createEffect(() =>
        this.actions$.pipe(
            ofType(addAddressesRequested),
            concatLatestFrom(() => this.store.select(selectAddresses)),
            map(([{ addresses, correlationId }, existing]) => {
                const merged = mergeNewAddressesIntoCollection(existing, addresses);
                if (!merged) {
                    return addressesUnchanged({ correlationId });
                }
                return saveAddressesRequested({ addresses: merged, correlationId });
            }),
        ),
    );

    /**
     * Narrows the collection to the scope one notification load just declared, once its per-notification
     * upserts have all landed (see `addressScopeRequested`'s doc and `NotificationEffects.mergeAddresses`).
     *
     * Modelled exactly on `mergeAddresses$`: reads the collection the upserts already wrote — effects
     * observe actions after their reducers ran, so the selector already holds them — applies
     * `markAddressesOutOfScope` against it, and forwards a genuine change to `saveAddressesRequested`
     * under the same correlation id, or settles the write as unchanged when the scope already matched.
     */
    public applyAddressScope$ = createEffect(() =>
        this.actions$.pipe(
            ofType(addressScopeRequested),
            concatLatestFrom(() => this.store.select(selectAddresses)),
            map(([{ inScopeIds, correlationId }, existing]) => {
                const addresses = markAddressesOutOfScope(existing, new Set(inScopeIds));
                if (!addresses) {
                    return addressesUnchanged({ correlationId });
                }
                return saveAddressesRequested({ addresses, correlationId });
            }),
        ),
    );

    // A manual timesheet entry used to carry its own copy of the address, which had to be folded
    // back into the collection (`syncManualEntryAddresses$`, now removed) so it did not drift from
    // the address book's copy. Now the entry stores only the address's id, already pointing into the
    // collection by construction: the picker only offers addresses the slice already holds, and hand
    // entry dispatches `addAddressesRequested` (see `AddressPickerComponent.saveNewAddress`) before
    // the id ever reaches the entry. There is nothing left to fold.

    /** Rehydrate the persisted collection into the slice on init, via the generic layer. */
    public loadAddresses$ = createEffect(() =>
        this.actions$.pipe(
            ofType(initAddresses),
            map(() => hydrateSlice({ featureKey: ADDRESS_FEATURE_KEY })),
        ),
    );

    /** Bridge the generic hydrate result into the address slice. */
    public hydrateAddresses$ = createEffect(() =>
        this.actions$.pipe(
            ofType(sliceHydrated),
            filter(({ featureKey }) => featureKey === ADDRESS_FEATURE_KEY),
            map(({ data }) => addressesLoadedFromStorage({ addresses: (data as AddressState[] | undefined) ?? [] })),
        ),
    );

    /** The store already holds the collection; mirror it to storage via the generic layer. */
    public saveAddresses$ = createEffect(() =>
        this.actions$.pipe(
            ofType(saveAddressesRequested),
            map(({ correlationId }) => persistSlice({ featureKey: ADDRESS_FEATURE_KEY, correlationId })),
        ),
    );

    /** Bridge the generic persist success/failure back to the address settle actions. */
    public addressPersisted$ = createEffect(() =>
        this.actions$.pipe(
            ofType(slicePersisted),
            filter(({ featureKey }) => featureKey === ADDRESS_FEATURE_KEY),
            map(({ correlationId }) => addressesSaved({ correlationId })),
        ),
    );

    public addressPersistError$ = createEffect(() =>
        this.actions$.pipe(
            ofType(persistenceError),
            filter(({ featureKey }) => featureKey === ADDRESS_FEATURE_KEY),
            map(({ error, correlationId }) => {
                this.log.error(`AddressEffects: addressPersistError$: ${error}`);
                return addressStorageSyncError({ error, correlationId });
            }),
        ),
    );
}
